// src/reporting/pdf/createCompassPdf.js

import jsPDF from "jspdf";
import logoBase64 from "assets/greenland-name-logo-base64";
import drawCompassLogo from "reporting/pdf/drawCompassLogo";
import { loadOrgPdfLogo } from "reporting/pdf/orgLogo";
import { PDF_STONE } from "reporting/pdf/pdfTheme";

const DEFAULT_SOURCE = "Source: Greenland Risk Management LLC";

const DEFAULT_DISCLAIMER =
  "This information is provided for informational purposes only and does not constitute trading, investment, accounting, legal or tax advice. This information does not constitute a recommendation, solicitation or municipal advisory service, and access does not create an advisory or fiduciary relationship. Forward-looking statements involve risks and uncertainties and results may differ from past performance. See more information at greenlandrm.com/disclosures.";

const DEFAULT_MEMBERSHIP = "Member NFA | SEC/MSRB Registered";

const DEFAULT_COPYRIGHT = `©${new Date().getFullYear()} Greenland Risk Management LLC. All rights reserved.`;

const DEFAULT_UNBRANDED_FOOTER = "Powered by CoreCompass";
const DEFAULT_UNBRANDED_COPYRIGHT = `©${new Date().getFullYear()} Core2tek LLC. All rights reserved.`;

const BRANDING_MODES = {
  grm: {
    showLogo: true,
    showSource: true,
    showDisclaimer: true,
    showMembership: true,
    showCopyright: true,
    showCompassIcon: false,

    sourceText: DEFAULT_SOURCE,
    disclaimerText: DEFAULT_DISCLAIMER,
    membershipText: DEFAULT_MEMBERSHIP,
    copyrightText: DEFAULT_COPYRIGHT,
  },

  unbranded: {
    showLogo: false,
    showSource: false,
    showDisclaimer: false,
    showMembership: false,
    showCopyright: false,
    showCompassIcon: true,

    sourceText: "",
    disclaimerText: "",
    membershipText: "",
    footerText: DEFAULT_UNBRANDED_FOOTER,
    copyrightText: DEFAULT_UNBRANDED_COPYRIGHT,
  },
};

export async function createCompassPdf({
  reportTitle = "CoreCompass Report",
  orgId = null,
  orgName = "",
  asOfLabel = "",
  createdStamp = new Date(),
  orientation = "portrait",

  // "grm" for TradeTracker
  // "unbranded" for DebtStudio/FundPlanner
  branding = "grm",

  // Optional overrides
  showLogo,
  showSource,
  showDisclaimer,
  showMembership,
  showCopyright,
  showCompassIcon,

  sourceText,
  disclaimerText,
  membershipText,
  copyrightText,
  contentMargins = {},
} = {}) {
  const orgLogo = await loadOrgPdfLogo(orgId);
  const doc = new jsPDF({
    unit: "pt",
    format: "letter",
    orientation,
  });

  let pageWidth = doc.internal.pageSize.getWidth();
  let pageHeight = doc.internal.pageSize.getHeight();

  const brandingConfig = {
    ...(BRANDING_MODES[branding] || BRANDING_MODES.grm),

    ...(showLogo != null ? { showLogo } : {}),
    ...(showSource != null ? { showSource } : {}),
    ...(showDisclaimer != null ? { showDisclaimer } : {}),
    ...(showMembership != null ? { showMembership } : {}),
    ...(showCopyright != null ? { showCopyright } : {}),
    ...(showCompassIcon != null ? { showCompassIcon } : {}),

    ...(sourceText != null ? { sourceText } : {}),
    ...(disclaimerText != null ? { disclaimerText } : {}),
    ...(membershipText != null ? { membershipText } : {}),
    ...(copyrightText != null ? { copyrightText } : {}),
  };

  const hasBrandedFooter =
    brandingConfig.showSource ||
    brandingConfig.showDisclaimer ||
    brandingConfig.showMembership ||
    brandingConfig.showCopyright;

  const defaultMargins = {
    left: 40,
    right: 40,
    top: 110,
    bottom: hasBrandedFooter ? 120 : 55,
  };

  let margins = {
    ...defaultMargins,
    ...contentMargins,
  };

  let usableWidth = pageWidth - margins.left - margins.right;
  let activePageMeta = {
    reportTitle,
    orgName,
    asOfLabel,
    margins: { ...margins },
  };
  const pageMeta = new Map([[1, activePageMeta]]);
  const originalAddPage = doc.addPage.bind(doc);

  const createdDisplay =
    createdStamp instanceof Date
      ? createdStamp.toLocaleString(undefined, {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZoneName: "short",
        })
      : String(createdStamp || "");

  function addHeader(meta, width) {
    const titleY = 50;
    const pageMargins = meta.margins;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(
      ...(branding === "unbranded" ? PDF_STONE.primary : [58, 89, 24])
    );

    // Compass icon for unbranded reports
    if (brandingConfig.showCompassIcon) {
      drawCompassLogo(doc, pageMargins.left, titleY - 15, 17);

      doc.text(meta.reportTitle, pageMargins.left + 20, titleY);
    } else {
      doc.text(meta.reportTitle, pageMargins.left, titleY);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(40);

    doc.text(meta.orgName || "—", pageMargins.left, 72);

    if (meta.asOfLabel) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(80);

      doc.text(meta.asOfLabel, pageMargins.left, 88);
    }

    // An uploaded organization mark takes precedence over the default mark.
    if (orgLogo) {
      try {
        const maxWidth = 140;
        const maxHeight = 45;
        const ratio = Math.min(maxWidth / orgLogo.width, maxHeight / orgLogo.height);
        const logoWidth = orgLogo.width * ratio;
        const logoHeight = orgLogo.height * ratio;
        doc.addImage(
          orgLogo.dataUrl,
          orgLogo.format,
          width - pageMargins.right - logoWidth,
          36,
          logoWidth,
          logoHeight
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Failed to render organization PDF logo:", err);
      }
    } else if (brandingConfig.showLogo) {
      try {
        doc.addImage(
          logoBase64,
          "PNG",
          width - 180,
          40,
          140,
          0
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Failed to render Compass PDF logo:", err);
      }
    }
  }

  function addFooter(pageNum, meta, width, height) {
    const pageMargins = meta.margins;
    const pageUsableWidth = width - pageMargins.left - pageMargins.right;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120);

    // Full GRM footer
    if (hasBrandedFooter) {
      const footerTop = height - 110;

      if (brandingConfig.showSource && brandingConfig.sourceText) {
        doc.text(brandingConfig.sourceText, pageMargins.left, footerTop);
      }

      if (brandingConfig.showDisclaimer && brandingConfig.disclaimerText) {
        doc.text(
          brandingConfig.disclaimerText,
          pageMargins.left,
          footerTop + 15,
          {
            maxWidth: pageUsableWidth,
          }
        );
      }

      if (brandingConfig.showMembership && brandingConfig.membershipText) {
        doc.text(
          brandingConfig.membershipText,
          pageMargins.left,
          footerTop + 60
        );
      }

      if (brandingConfig.showCopyright && brandingConfig.copyrightText) {
        doc.text(
          brandingConfig.copyrightText,
          pageMargins.left,
          footerTop + 75
        );
      }

      doc.text(
        `Report created ${createdDisplay}`,
        width - pageMargins.right,
        footerTop + 75,
        {
          align: "right",
        }
      );

      doc.text(
        `Page ${pageNum}`,
        width - pageMargins.right,
        footerTop + 90,
        {
          align: "right",
        }
      );

      return;
    }

    // Minimal product footer for DebtStudio/FundPlanner reports
    const footerY = height - 42;

    if (brandingConfig.footerText) {
      doc.text(brandingConfig.footerText, pageMargins.left, footerY);
    }

    if (brandingConfig.copyrightText) {
      doc.text(brandingConfig.copyrightText, pageMargins.left, footerY + 11);
    }

    doc.text(
      `Page ${pageNum}`,
      width - pageMargins.right,
      footerY + 11,
      {
        align: "right",
      }
    );
  }

  function finalize() {
    const totalPages = doc.internal.getNumberOfPages();

    for (let p = 1; p <= totalPages; p += 1) {
      doc.setPage(p);
      const meta = pageMeta.get(p) || activePageMeta;
      if (meta.decorate === false) continue;
      const width = doc.internal.pageSize.getWidth();
      const height = doc.internal.pageSize.getHeight();
      addHeader(meta, width);
      addFooter(p, meta, width, height);
    }
  }

  const ctx = {
    doc,
    margins,
    pageWidth,
    pageHeight,
    usableWidth,
    finalize,
  };

  function syncLayout(nextMargins = margins) {
    pageWidth = doc.internal.pageSize.getWidth();
    pageHeight = doc.internal.pageSize.getHeight();
    margins = { ...nextMargins };
    usableWidth = pageWidth - margins.left - margins.right;
    Object.assign(ctx, { margins, pageWidth, pageHeight, usableWidth });
  }

  doc.addPage = (...args) => {
    originalAddPage(...args);
    syncLayout(activePageMeta.margins);
    pageMeta.set(doc.internal.getNumberOfPages(), activePageMeta);
    return doc;
  };

  ctx.startSection = ({
    reportTitle: nextTitle = reportTitle,
    orgName: nextOrgName = orgName,
    asOfLabel: nextAsOfLabel = asOfLabel,
    orientation: nextOrientation = "portrait",
    contentMargins: nextContentMargins = {},
    addPage = true,
    decorate = true,
  } = {}) => {
    const nextMargins = { ...defaultMargins, ...nextContentMargins };
    activePageMeta = {
      reportTitle: nextTitle,
      orgName: nextOrgName,
      asOfLabel: nextAsOfLabel,
      margins: nextMargins,
      decorate,
    };
    if (addPage) {
      doc.addPage("letter", nextOrientation);
    } else {
      syncLayout(nextMargins);
      pageMeta.set(doc.internal.getCurrentPageInfo().pageNumber, activePageMeta);
    }
    return ctx;
  };

  return ctx;
}

export default createCompassPdf;
